import React, { useState } from 'react'

import { Modal, Row } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import deleteCoverageProviderForMonth from 'core/application/expenses/deleteCoverageProviderForMonth'
import insertCoverageProviderForMonth from 'core/application/expenses/insertCoverageProviderForMonth'

import MecExplanation from 'common/components/benefit/Expenses/MecExplanation'
import notification from 'common/components/feedback/Notification'
import Icon from 'common/components/general/Icon'

import useCommandRequest from 'common/hooks/useCommandRequest'

import ExpenseTypeRadio from '../../../getReimbursed/expenseTypeRadio'
import ExpenseCoverageProviderSelect from '../ExpenseCoverageProviderSelect'
import { ModalTaxSaving } from '../interfaces'

export const ME_VALUE: string = 'me'

type ExpenseCoverageModalProps = {
  isVisible: boolean
  taxSaving: ModalTaxSaving
  year: number
  onFinish: () => void
  onClose: () => void
}

const ExpenseCoverageModal = ({ isVisible, taxSaving, year, onFinish, onClose }: ExpenseCoverageModalProps) => {
  const month = taxSaving.user.items[taxSaving.colKey].month
  const status = taxSaving.user.items[taxSaving.colKey].status
  const providerName = taxSaving.user.items[taxSaving.colKey].providerName
  const date = taxSaving.user.items[taxSaving.colKey].date

  const [sendExpenseCoverageProvider] = useCommandRequest()

  const formattedMonth = moment(month, 'MMM').format('MMMM')
  const [showMore, setShowMore] = useState(false)

  const handleSubmit = async (values: any) => {
    let result = null
    if (values?.mec)
      result = await sendExpenseCoverageProvider(() => insertCoverageProviderForMonth({ startDate: date, ...values }))
    else
      result = await sendExpenseCoverageProvider(() =>
        deleteCoverageProviderForMonth({ startDate: date, familyMemberId: values?.familyMemberId }),
      )

    if (result?.completed) {
      onFinish()
      notification.open({
        type: 'success',
        message: 'Expense coverage status saved successfully',
      })
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        mec: status === 1,
        familyMemberId: taxSaving.user.familyMemberId,
        providerName: providerName,
      }}
      validationSchema={Yup.object().shape({
        mec: Yup.boolean(),
        providerName: Yup.string().nullable().required('Provider name is required'),
      })}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        return (
          <Modal
            open={isVisible}
            title={`${formattedMonth} ${year}`}
            okText="Save"
            onCancel={onClose}
            onOk={() => handleSubmit(formik.values)}
            okButtonProps={{
              disabled:
                (!formik.isValid && formik.values?.mec) ||
                (!formik.values?.mec && !providerName) ||
                (providerName === formik.values?.providerName && formik.values?.mec === (status === 1)),
            }}
          >
            <div>
              <ExpenseTypeRadio
                month={formattedMonth}
                yesText="Yes"
                noText="No"
                mec={formik.values?.mec}
                name={formik.values.familyMemberId === null ? 'you' : taxSaving.user.user.name}
              />
              {formik.values?.mec && (
                <ExpenseCoverageProviderSelect
                  name="providerName"
                  defaultValue={providerName}
                  value={formik.values?.providerName}
                  date={date}
                  familyMemberId={formik.values.familyMemberId !== ME_VALUE ? formik.values.familyMemberId : null}
                />
              )}
              <Row>
                <div className="tax-savings-view-more">
                  <div
                    className="tax-savings-view-more-button color-green-6"
                    onClick={() => setShowMore((previous) => !previous)}
                  >
                    <div className="tax-savings-view-more-text">What is minimum essential coverage?</div>
                    {showMore ? (
                      <Icon type="far fa-angle-up" className="ml-4" fontAwesome />
                    ) : (
                      <Icon type="far fa-angle-down" className="ml-4" fontAwesome />
                    )}
                  </div>
                </div>
                {showMore && (
                  <div className="mt-16">
                    <MecExplanation />
                  </div>
                )}
              </Row>
            </div>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default ExpenseCoverageModal
